import { useState, useEffect, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_equestrian-hub-20/artifacts/0lyoa6pm_No%20hatter%2C%20feher.png";

function LiveDisplay() {
  const [displayData, setDisplayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [localStopwatch, setLocalStopwatch] = useState(0);
  const stopwatchRef = useRef(null);
  const lastStartTimeRef = useRef(null);

  // Update clock every second
  useEffect(() => {
    const clockInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(clockInterval);
  }, []);

  // Poll for live display data every second
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/competition/live-display`);
        setDisplayData(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch live display data");
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 1000);
    return () => clearInterval(interval);
  }, []);

  // Synchronized stopwatch - runs locally when stopwatch_running is true
  useEffect(() => {
    if (!displayData) return;

    if (displayData.stopwatch_running && displayData.stopwatch_start_time) {
      // If stopwatch is running and we have a start time
      if (lastStartTimeRef.current !== displayData.stopwatch_start_time) {
        // New stopwatch session started - sync with server time
        lastStartTimeRef.current = displayData.stopwatch_start_time;
      }
      
      // Start local interval if not already running
      if (!stopwatchRef.current) {
        stopwatchRef.current = setInterval(() => {
          const now = Date.now() / 1000;
          const elapsed = now - displayData.stopwatch_start_time;
          setLocalStopwatch(elapsed > 0 ? elapsed : 0);
        }, 10); // Update every 10ms for smooth display
      }
    } else {
      // Stopwatch stopped - clear interval and use server time
      if (stopwatchRef.current) {
        clearInterval(stopwatchRef.current);
        stopwatchRef.current = null;
      }
      // When stopped, use the final time from server
      if (displayData.time_seconds !== null && displayData.time_seconds !== undefined) {
        setLocalStopwatch(displayData.time_seconds);
      }
    }

    return () => {
      if (stopwatchRef.current) {
        clearInterval(stopwatchRef.current);
        stopwatchRef.current = null;
      }
    };
  }, [displayData?.stopwatch_running, displayData?.stopwatch_start_time, displayData?.time_seconds]);

  // Format current date/time
  const formatDateTime = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}.${month}.${day}. ${hours}:${minutes}:${seconds}`;
  };

  // Format time from seconds to mm:ss.cc
  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return "--:--.--";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const centis = Math.floor((seconds * 100) % 100);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${centis.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <img src={LOGO_URL} alt="Gepárd" className="w-32 animate-pulse" />
      </div>
    );
  }

  if (!displayData || !displayData.active) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center">
        <img src={LOGO_URL} alt="Gepárd" className="w-48 mb-8" />
        <p className="text-white/40 text-2xl">Nincs aktív verseny</p>
      </div>
    );
  }

  // Use local stopwatch when running, otherwise use server time
  const displayTime = displayData.stopwatch_running ? localStopwatch : (displayData.time_seconds ?? 0);

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Logo in corner */}
      <div className="absolute top-6 left-6">
        <img src={LOGO_URL} alt="Gepárd" className="h-16" />
      </div>

      {/* Date/Time in top right corner */}
      <div className="absolute top-6 right-6 text-white/60 text-lg font-mono">
        {formatDateTime(currentTime)}
      </div>

      {/* Main content */}
      <div className="flex flex-col items-center justify-center min-h-screen px-8">
        {/* Event name - large */}
        <h1 className="text-5xl md:text-7xl font-bold text-[#D4AF37] text-center mb-4">
          {displayData.event_name}
        </h1>
        
        {/* Scoring type - smaller */}
        <p className="text-xl md:text-2xl text-white/60 mb-16">
          {displayData.judging_type}
        </p>

        {/* Current rider info */}
        <div className="flex items-center justify-center gap-16 md:gap-32">
          {/* Left side: Start number and rider info */}
          <div className="text-center">
            {/* Start number */}
            <div className="text-9xl md:text-[14rem] font-bold text-[#D4AF37] leading-none mb-6">
              {displayData.start_number}
            </div>
            
            {/* Rider name - very large */}
            <h2 className="text-5xl md:text-8xl font-bold text-white mb-4">
              {displayData.rider_name}
            </h2>
            
            {/* Horse name - large */}
            <p className="text-3xl md:text-6xl text-white/80">
              {displayData.horse_name}
            </p>
          </div>

          {/* Right side: Time and faults */}
          <div className="text-center space-y-8">
            {/* Time */}
            <div>
              <p className="text-2xl text-white/40 mb-2">Idő</p>
              <div className={`text-7xl md:text-9xl font-mono font-bold ${displayData.stopwatch_running ? 'text-green-400' : 'text-white'}`}>
                {formatTime(displayTime)}
              </div>
            </div>
            
            {/* Fault points */}
            <div>
              <p className="text-2xl text-white/40 mb-2">Hibapont</p>
              <div className="text-7xl md:text-9xl font-bold text-[#D4AF37]">
                {displayData.fault_points ?? 0}
              </div>
            </div>
          </div>
        </div>

        {/* Status indicator */}
        {(displayData.status || displayData.stopwatch_running) && (
          <div className={`mt-16 px-8 py-4 rounded-lg text-3xl font-bold ${
            displayData.status === 'kizarva' ? 'bg-red-600 text-white' :
            displayData.status === 'feladta' ? 'bg-orange-600 text-white' :
            displayData.stopwatch_running ? 'bg-green-600 text-white animate-pulse' :
            'bg-green-600 text-white'
          }`}>
            {displayData.status === 'kizarva' ? 'KIZÁRVA' :
             displayData.status === 'feladta' ? 'FELADTA' :
             displayData.stopwatch_running ? 'FOLYAMATBAN' : ''}
          </div>
        )}
      </div>
    </div>
  );
}

export default LiveDisplay;
